import type { ProfilerOnRenderCallback } from 'react';

export type HydrationPhaseTiming = {
	// performance.now() timestamps
	start: number;
	end: number;
	// From render start to commit start
	renderMs: number;
	// From commit start to the Profiler's onRender, i.e. DOM updates and useLayoutEffect
	commitMs: number;
	// Time React spent rendering components, excluding yields and React overhead
	actualRenderMs: number;
	commits: number;
};

export type HydrationPhaseTimings = {
	shell: HydrationPhaseTiming;
	// Absent when nothing hydrated synchronously after the shell, e.g. without SSR streaming
	syncBoundaries?: HydrationPhaseTiming;
};

const createPhase = (
	actualDuration: number,
	startTime: number,
	commitTime: number,
): HydrationPhaseTiming => {
	const end = performance.now();
	return {
		start: startTime,
		end,
		renderMs: commitTime - startTime,
		commitMs: end - commitTime,
		actualRenderMs: actualDuration,
		commits: 1,
	};
};

/**
 * Times the two hydration phases from a React Profiler wrapping the app:
 * - Shell: the first commit, hydrating the root and leaving suspense boundaries dehydrated.
 * - Sync boundaries: every further commit in the same task. `scheduleSyncHydration` hydrates the
 *   resolved boundaries synchronously at the end of the shell commit, so they commit before the
 *   task ends. Nested boundaries can add more commits.
 *
 * `onComplete` runs once, in a microtask after that task, and later commits are ignored.
 */
export function createHydrationPhaseTimer(
	onComplete: (timings: HydrationPhaseTimings) => void,
): ProfilerOnRenderCallback {
	let shell: HydrationPhaseTiming | undefined;
	let syncBoundaries: HydrationPhaseTiming | undefined;
	let isComplete = false;

	return (_id, _phase, actualDuration, _baseDuration, startTime, commitTime) => {
		if (isComplete) {
			return;
		}
		const phase = createPhase(actualDuration, startTime, commitTime);

		if (!shell) {
			shell = phase;
			queueMicrotask(() => {
				isComplete = true;
				onComplete({ shell: phase, syncBoundaries });
			});
			return;
		}

		syncBoundaries = syncBoundaries
			? {
					start: syncBoundaries.start,
					end: phase.end,
					renderMs: syncBoundaries.renderMs + phase.renderMs,
					commitMs: syncBoundaries.commitMs + phase.commitMs,
					actualRenderMs: syncBoundaries.actualRenderMs + phase.actualRenderMs,
					commits: syncBoundaries.commits + 1,
				}
			: phase;
	};
}
